import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FetchUsersQuery, FetchUsersQueryVariables, FetchUsersDocument, GetPaginatedConfigAdministratorsQuery, GetPaginatedConfigAdministratorsQueryVariables, GetPaginatedConfigAdministratorsDocument } from "@/graphql/__generated__/graphql";
import { Dispatch, SetStateAction, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AddConfigAdminsDefaultValues, AddConfigAdminsSchema, TAddConfigAdminsSchema } from "../Models/AddConfigAdmins";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { onInvalid } from "@/utils/form";
import { Separator } from "@/components/ui/separator";
import { useAddConfigAdministratorsMutationHook } from "@/hooks/mutations";
import { ComboboxInputField } from "@/components/shared/form";
import { useQuery } from "@apollo/client/react";

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

const AddConfigAdminModal = ({ open, setOpen }: Props) => {
  // Track whether to close dialog after successful submission
  const shouldCloseAfterSubmit = useRef(false);

  // Initialize form
  const form = useForm<TAddConfigAdminsSchema>({
    resolver: zodResolver(AddConfigAdminsSchema),
    defaultValues: AddConfigAdminsDefaultValues,
    mode: "onChange",
  });

  // Hooks
  const { t } = useTranslation(["modals", "common"]);

  // Mutation
  const { addConfigAdministratorMutation } = useAddConfigAdministratorsMutationHook();

  // Fetch all existing config admins
  const { data: configAdmins } = useQuery<GetPaginatedConfigAdministratorsQuery, GetPaginatedConfigAdministratorsQueryVariables>(GetPaginatedConfigAdministratorsDocument, {
    variables: {
      first: 100,
    },
    fetchPolicy: "cache-and-network",
  });

  // Fetch all users from the database
  const { data: users, loading: usersLoading } = useQuery<FetchUsersQuery, FetchUsersQueryVariables>(FetchUsersDocument, {
    variables: {
      first: 100,
    },
    fetchPolicy: "cache-and-network",
  });

  // Filter out users that are already config admins
  const usersArr = users?.fetchUsers?.filter((user) => !configAdmins?.paginatedConfigAdministrators?.edges?.some((admin) => admin.node.id === user.id)) || [];

  /**
   * Handle the form submission
   * @param {TAddConfigAdminsSchema} data - The form data
   * @returns {Promise<void>} - A promise that resolves when the form is submitted
   */
  const onSubmit = async (data: TAddConfigAdminsSchema): Promise<void> => {
    // Guard clause to ensure userId is provided
    if (!data.userId) {
      return;
    }

    try {
      form.reset();
      await addConfigAdministratorMutation({ variables: { userId: Number(data.userId) } });

      // Only close dialog if the "Add and Close" button was clicked
      if (shouldCloseAfterSubmit.current) {
        setOpen(false);
      }
    } catch (error) {
      // Handle error if needed - don't close dialog on error
      console.error("Failed to add config administrator:", error);
    } finally {
      // Reset the flag
      shouldCloseAfterSubmit.current = false;
    }
  };

  /**
   * Handle the "Add and Close" button click
   * @returns void
   */
  const handleAddAndClose = (): void => {
    shouldCloseAfterSubmit.current = true;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("addconfigadmin.title")}</DialogTitle>
          <DialogDescription>
            {t("addconfigadmin.description")}
          </DialogDescription>
          <Separator />
        </DialogHeader>
        <Form {...form}>
          <form id="add-config-admin-form" className="space-y-4" onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
            <FormField
              control={form.control}
              name="userId"
              render={() => (
                <FormItem>
                  <ComboboxInputField
                    form={form}
                    name="userId"
                    className="w-full"
                    label={t("addconfigadmin.inputs.users.label")}
                    options={usersArr.map((user) => ({
                      label: `${user.firstname} ${user.lastname}`,
                      value: user.id,
                    }))}
                    disabled={usersLoading || usersArr.length === 0}
                    loading={usersLoading}
                    value={form.watch("userId")}
                    numberValue
                    placeholder={usersArr.length === 0 ? t("common:nodataavailable") : t("addconfigadmin.inputs.users.placeholder")}
                    popoverProps={{
                      onWheel: (e) => {
                        e.stopPropagation();
                      },
                    }}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        <DialogFooter className="select-none">
          <DialogClose asChild>
            <Button variant="outline"
              onClick={() => {
                form.reset();
              }}>
              {t("common:buttons.close")}
            </Button>
          </DialogClose>
          <Button
            type="submit"
            variant="default"
            form="add-config-admin-form"
            disabled={!form.watch("userId")}
          >
            {t("common:buttons.add")}
          </Button>
          <Button
            type="submit"
            variant="default"
            form="add-config-admin-form"
            onClick={handleAddAndClose}
            disabled={!form.watch("userId")}
          >
            {t("common:buttons.addandclose")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddConfigAdminModal;
